import { useVisibilityObserver } from "client/Hooks";
import {
	ClientBrandingConfiguration,
	WorkspaceHeaderStyle,
	userConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import styles from "./WorkspaceLogo.module.less";
import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";

type WorkspaceLogoProps = {
	workspaceHeaderStyle?: WorkspaceHeaderStyle;
};

export const WorkspaceLogo = (props: WorkspaceLogoProps): ReactElement => {
	const {
		workspaceHeaderStyle
	} = props;

	const [hasErrorForCustomLogo, setHasErrorForCustomLogo] = useState(false);

	const {
		homeLogoId,
		darkThemeHomeLogoId,
		productName
	} = useMemo(
		(): ClientBrandingConfiguration => {
			return userConfiguration().branding;
		},
		[]
	);

	const customLogoErrorHandler = useCallback(
		(): void => {
			setHasErrorForCustomLogo(true);
		},
		[]
	);

	return (
		<>
			{homeLogoId && (
				<CustomLogo
					customLogoId={homeLogoId}
					productName={productName}
					hasError={hasErrorForCustomLogo}
					onError={customLogoErrorHandler}
					theme="light"
				/>
			)}
			{darkThemeHomeLogoId && (
				<CustomLogo
					customLogoId={darkThemeHomeLogoId}
					productName={productName}
					hasError={hasErrorForCustomLogo}
					onError={customLogoErrorHandler}
					theme="dark"
				/>
			)}
			<DefaultLogo
				shouldBeAlwaysVisible={hasErrorForCustomLogo}
				shouldBeVisibleInLightTheme={!homeLogoId}
				shouldBeVisibleInDarkTheme={!darkThemeHomeLogoId}
				headerStyle={workspaceHeaderStyle}
			/>
		</>
	);
};
WorkspaceLogo.displayName = "WorkspaceLogo";

type CustomLogoProps = {
	customLogoId: string;
	productName: string;
	hasError: boolean;
	onError(): void;
	theme: "light" | "dark";
};

const CustomLogo = (props: CustomLogoProps): ReactElement => {
	const {
		customLogoId,
		productName,
		hasError,
		onError,
		theme
	} = props;

	const wrapperRef: RefObject<HTMLSpanElement | null> = useRef<HTMLSpanElement>(null);

	const isVisible: boolean = useVisibilityObserver(wrapperRef);

	const customLogoUrl = useMemo(
		(): string => {
			return createUrlRelativeToEnvironment(`Branding/Logo?id=${encodeURIComponent(customLogoId)}`);
		},
		[customLogoId]
	);

	return (
		<span
			{...setTrackingId("custom", `|${theme}`)}
			ref={wrapperRef}
			className={classNames(
				styles.customLogoWrapper,
				{
					[styles.customLogoWrapper_light]: theme === "light",
					[styles.customLogoWrapper_dark]: theme === "dark",
					[styles.customLogoWrapper_hasError]: hasError
				}
			)}
		>
			{isVisible && (
				<img
					src={customLogoUrl}
					alt={productName}
					onError={onError}
				/>
			)}
		</span>
	);
};
CustomLogo.displayName = "CustomLogo";

type DefaultLogoProps = {
	shouldBeAlwaysVisible: boolean;
	shouldBeVisibleInLightTheme: boolean;
	shouldBeVisibleInDarkTheme: boolean;
	headerStyle?: WorkspaceHeaderStyle;
};

const DefaultLogo = (props: DefaultLogoProps): ReactElement => {
	const {
		shouldBeAlwaysVisible,
		shouldBeVisibleInLightTheme,
		shouldBeVisibleInDarkTheme,
		headerStyle
	} = props;

	return (
		<svg
			{...setTrackingId("default")}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 92 70.35"
			className={classNames(
				styles.defaultLogo,
				{
					[styles.defaultLogo_visibleInLightTheme]: shouldBeVisibleInLightTheme,
					[styles.defaultLogo_visibleInDarkTheme]: shouldBeVisibleInDarkTheme,
					[styles.defaultLogo_alwaysVisible]: shouldBeAlwaysVisible,
					[styles.defaultLogo_coloredHeader]: headerStyle === WorkspaceHeaderStyle.ColoredBackground
				}
			)}
		>
			<g>
				<rect
					className={styles.defaultLogoPart_header}
					width="43.29"
					height="70.35"
					rx="4.33"
				/>
				<rect
					className={styles.defaultLogoPart_header}
					x="48.71"
					width="43.29"
					height="46"
					rx="4.33"
				/>
				<rect
					className={styles.defaultLogoPart_primary}
					x="48.71"
					y="51.41"
					width="18.94"
					height="18.94"
					rx="4.33"
				/>
				<rect
					className={styles.defaultLogoPart_primary}
					x="73.06"
					y="51.41"
					width="18.94"
					height="18.94"
					rx="4.33"
				/>
			</g>
		</svg>
	);
};
DefaultLogo.displayName = "DefaultLogo";